import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, Check, CheckCircle2, Copy, Loader2, ListChecks, Plus, RotateCcw, Search,
  Users, XCircle,
} from 'lucide-react';
import client, { createIdempotencyKey } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { freespinRewardText, isFreespinOnlyBonus } from '../lib/bonusReward';
import ConfirmDialog from './components/ConfirmDialog';
import { formatMoney } from './lib/formatters';

/*
 * Toplu bonus: bir etkinliğin kazanan listesi gibi alt alta yazılmış
 * kullanıcı adlarına aynı bonusu verir.
 *
 * Akış iki adımdır. Önce liste üyelere çözülür (para hareketi yok) ve
 * bulunamayan adlar gösterilir; yönetici listeyi düzeltmeden bonus
 * verilmez. Sonra çözülen üyeler parça parça gönderilir — her üye tekli
 * *Bonus Ver* ile aynı yoldan geçer. Partinin anahtarı (`batchKey`) liste ya
 * da bonus değişene kadar sabittir: kopan parçayı yeniden göndermek ya da
 * düğmeye ikinci kez basmak bonusu almış üyeye tekrar vermez.
 */

const blankGrant = {
  mode: 'campaign', bonus_id: '', title: '', amount: 1000,
  wager_multiplier: 0, note: '',
  // Bakiye eşiği: açıkken ana + bonus bakiyesi eşiğin üstündeki üye atlanır.
  balance_limit: false, max_balance: 0,
};

// Sohbetten kopyalanan liste satır, virgül ya da noktalı virgülle ayrılır.
export const splitUsernames = (text) => String(text || '')
  .split(/[\s,;]+/)
  .map((item) => item.replace(/^@+/, '').trim())
  .filter(Boolean);

// Arama sonucundan seçilen ad listeye yeni satır olarak eklenir; listede
// zaten varsa (harf duyarsız) çıkarılır. Liste tek kaynaktır: elle yazılan
// ile seçilen ayrı tutulsaydı önizleme ikisini ayrı ayrı çözmek zorunda
// kalırdı.
export const toggleUsername = (text, username) => {
  const key = username.toLocaleLowerCase('tr');
  const names = splitUsernames(text);
  if (names.some((name) => name.toLocaleLowerCase('tr') === key)) {
    return names.filter((name) => name.toLocaleLowerCase('tr') !== key).join('\n');
  }
  return [...names, username].join('\n');
};

export const addUsernames = (text, usernames) => {
  const names = splitUsernames(text);
  const seen = new Set(names.map((name) => name.toLocaleLowerCase('tr')));
  usernames.forEach((name) => {
    const key = name.toLocaleLowerCase('tr');
    if (!seen.has(key)) { seen.add(key); names.push(name); }
  });
  return names.join('\n');
};

// Hızlı listeler. Bonus alamayacak hesapları (engelli, pasif, bonus yasaklı)
// sunucu baştan ayıklar; "yatırım" onaylanmış gerçek yatırımdır, elle
// bakiye ekleme sayılmaz.
export const QUICK_SEGMENTS = [
  { id: 'all', label: 'Tüm üyeler' },
  { id: 'depositors', label: 'Yatırımı olan üyeler' },
  { id: 'deposit_today', label: 'Bugün yatırım yapanlar' },
  { id: 'deposit_7d', label: 'Son 7 gün yatırım yapanlar' },
  { id: 'deposit_30d', label: 'Son 30 gün yatırım yapanlar' },
];

// Bu kadar üyeyi aşan dağıtım onay penceresinde ifade yazdırır: hızlı
// listeyle tek tıkta binlerce üyeye para yazılabilir.
const CONFIRM_PHRASE_FROM = 100;

const BulkBonus = () => {
  const [text, setText] = useState('');
  const [bonuses, setBonuses] = useState([]);
  const [grant, setGrant] = useState(blankGrant);
  const [preview, setPreview] = useState(null);
  const [checking, setChecking] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [found, setFound] = useState([]);
  const [searching, setSearching] = useState(false);
  const [segmentBusy, setSegmentBusy] = useState('');
  const [segment, setSegment] = useState(null);
  const batchKey = useRef(createIdempotencyKey());
  // Hızlı liste metni ve önizlemeyi birlikte yazar; metin değişince
  // önizlemeyi silen etki o tek değişiklikte atlanır.
  const keepPreview = useRef(false);
  const { toast } = useToast();

  useEffect(() => {
    client.get('/admin/bonuses')
      .then(({ data }) => {
        const items = data.items || [];
        setBonuses(items);
        const first = items.find((item) => item.active) || items[0];
        if (first) {
          setGrant((current) => ({
            ...current,
            bonus_id: first.id,
            amount: isFreespinOnlyBonus(first) ? 0 : current.amount,
          }));
        }
      })
      .catch((error) => toast({
        title: 'Kampanyalar alınamadı',
        description: error.response?.data?.detail || 'Bir hata oluştu',
      }));
  }, [toast]);

  // Liste ya da bonus değişince yeni bir partidir.
  useEffect(() => {
    batchKey.current = createIdempotencyKey();
    setResults([]);
  }, [text, grant]);
  useEffect(() => {
    if (keepPreview.current) { keepPreview.current = false; return; }
    setPreview(null);
    setSegment(null);
  }, [text]);

  // Dağıtım tarayıcıdan parça parça yürür ve üye sayısında sınır yoktur:
  // sekme kapanırsa kalan üyeler işlenmez. Çıkış o sürede sorulur.
  useEffect(() => {
    if (!running) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [running]);

  // Arama yazmayı bekler; her tuşta istek atılmaz.
  useEffect(() => {
    const term = search.trim().replace(/^@+/, '');
    if (term.length < 2) { setFound([]); return undefined; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await client.get('/admin/bonus-grants/users', { params: { q: term } });
        if (!cancelled) setFound(data.items || []);
      } catch {
        if (!cancelled) setFound([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [search]);

  const typed = useMemo(() => splitUsernames(text), [text]);
  const typedKeys = useMemo(
    () => new Set(typed.map((name) => name.toLocaleLowerCase('tr'))),
    [typed],
  );
  const selectedCampaign = useMemo(
    () => bonuses.find((item) => item.id === grant.bonus_id),
    [bonuses, grant.bonus_id],
  );
  const freespinOnly = grant.mode === 'campaign' && isFreespinOnlyBonus(selectedCampaign);
  const multiplier = grant.mode === 'campaign'
    ? Number(selectedCampaign?.wager_multiplier || 0)
    : Number(grant.wager_multiplier || 0);
  const amount = freespinOnly ? 0 : Number(grant.amount || 0);
  const targets = useMemo(() => preview?.found || [], [preview]);
  const nameById = useMemo(
    () => Object.fromEntries(targets.map((row) => [row.id, row.username])),
    [targets],
  );

  const loadSegment = async (item) => {
    setSegmentBusy(item.id);
    try {
      const { data } = await client.post('/admin/bonus-grants/bulk/segment', { segment: item.id });
      if (!data.found?.length) {
        toast({ title: `${item.label}: üye yok` });
        return;
      }
      const next = data.found.map((row) => row.username).join('\n');
      // Metin aynıysa etki hiç çalışmaz; bayrak açık kalırsa bir sonraki
      // elle düzeltme eski önizlemeyi silmezdi.
      if (next !== text) {
        keepPreview.current = true;
        setText(next);
      }
      setPreview(data);
      setSegment({ label: item.label, excluded: Number(data.excluded || 0) });
    } catch (error) {
      toast({ title: 'Liste alınamadı', description: error.response?.data?.detail || 'Bir hata oluştu' });
    } finally { setSegmentBusy(''); }
  };

  const checkList = async () => {
    if (!typed.length) {
      toast({ title: 'Kullanıcı adlarını alt alta yapıştırın' });
      return;
    }
    setChecking(true);
    try {
      const { data } = await client.post('/admin/bonus-grants/bulk/preview', { usernames: typed });
      setPreview(data);
    } catch (error) {
      toast({ title: 'Liste kontrol edilemedi', description: error.response?.data?.detail || 'Bir hata oluştu' });
    } finally { setChecking(false); }
  };

  const formProblem = () => {
    if (!targets.length) return 'Önce listeyi kontrol edin';
    if (grant.mode === 'campaign' && !grant.bonus_id) return 'Bonus kampanyası seçin';
    if (grant.mode === 'custom' && !grant.title.trim()) return 'Özel bonus başlığını girin';
    if (!freespinOnly && (!Number.isFinite(amount) || amount <= 0)) return 'Geçerli bir bonus tutarı girin';
    if (grant.balance_limit && !(Number(grant.max_balance) >= 0)) return 'Geçerli bir bakiye eşiği girin';
    return '';
  };

  const openConfirm = () => {
    const problem = formProblem();
    if (problem) { toast({ title: problem }); return; }
    setConfirmOpen(true);
  };

  const run = async (userIds) => {
    setConfirmOpen(false);
    setRunning(true);
    setProgress({ done: 0, total: userIds.length });
    const size = Number(preview?.chunk_size) || 25;
    const body = {
      mode: grant.mode,
      bonus_id: grant.mode === 'campaign' ? grant.bonus_id : null,
      title: grant.mode === 'custom' ? grant.title.trim() : null,
      amount,
      wager_multiplier: grant.mode === 'custom' ? Number(grant.wager_multiplier || 0) : 0,
      note: grant.note.trim(),
      max_balance: grant.balance_limit ? Number(grant.max_balance || 0) : null,
    };
    const collected = new Map(results.map((row) => [row.user_id, row]));
    for (let start = 0; start < userIds.length; start += size) {
      const chunk = userIds.slice(start, start + size);
      try {
        const { data } = await client.post(
          '/admin/bonus-grants/bulk',
          { ...body, user_ids: chunk },
          { headers: { 'Idempotency-Key': batchKey.current } },
        );
        (data.results || []).forEach((row) => collected.set(row.user_id, row));
      } catch (error) {
        // Parçanın tamamı düştü (ağ, yetki, kampanya silinmiş): satırlar
        // başarısız işaretlenir, aynı anahtarla yeniden denenebilir.
        const detail = error.response?.data?.detail;
        const message = typeof detail === 'string' ? detail : 'İstek tamamlanamadı';
        chunk.forEach((id) => collected.set(id, { user_id: id, ok: false, error: message }));
      }
      setProgress({ done: Math.min(start + size, userIds.length), total: userIds.length });
      setResults([...collected.values()]);
    }
    setRunning(false);
    const all = [...collected.values()];
    const given = all.filter((row) => row.ok).length;
    const skipped = all.filter((row) => row.skipped).length;
    const failed = all.length - given - skipped;
    const parts = [`${given} üyeye verildi`];
    if (skipped) parts.push(`${skipped} bakiye eşiği nedeniyle atlandı`);
    if (failed) parts.push(`${failed} başarısız`);
    toast({ title: parts.join(', ') });
  };

  // Bakiye eşiğine takılan üye başarısız sayılmaz: yeniden denemek aynı
  // sonucu verir, ayrı listelenir.
  const skippedRows = results.filter((row) => row.skipped);
  const failedRows = results.filter((row) => !row.ok && !row.skipped);
  const okRows = results.filter((row) => row.ok);
  const replayCount = okRows.filter((row) => row.idempotent_replay).length;

  const copyMissing = async () => {
    try {
      await navigator.clipboard.writeText((preview?.missing || []).join('\n'));
      toast({ title: 'Bulunamayan adlar kopyalandı' });
    } catch { toast({ title: 'Kopyalanamadı' }); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl border border-violet-400/20 bg-violet-400/10 text-violet-300"><Users size={18} /></div>
        <div>
          <h1 className="text-xl font-bold">Toplu Bonus</h1>
          <p className="text-xs text-slate-500">Alt alta yazılmış kullanıcı adlarına aynı bonusu tek seferde verir.</p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="admin-card space-y-3 p-5">
          <div className="flex items-center gap-2">
            <h2 className="font-bold">1. Kullanıcı listesi</h2>
            <span className="ml-auto text-xs text-slate-500">{typed.length} ad</span>
          </div>
          <div className="space-y-2">
            <div className="text-xs text-slate-400">Hızlı liste</div>
            <div className="flex flex-wrap gap-1.5">
              {QUICK_SEGMENTS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  disabled={running || Boolean(segmentBusy)}
                  onClick={() => loadSegment(item)}
                  className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${segment?.label === item.label ? 'border-cyan-400/50 bg-cyan-400/10 text-cyan-300' : 'border-white/10 text-slate-300 hover:border-cyan-400/40'}`}
                >
                  {segmentBusy === item.id ? <Loader2 size={12} className="animate-spin" /> : <Users size={12} />}
                  {item.label}
                </button>
              ))}
            </div>
            {segment && (
              <p className="text-xs text-slate-500" aria-live="polite">
                {segment.label}: {targets.length} üye listeye yazıldı
                {segment.excluded > 0 ? ` · ${segment.excluded} hesap (engelli, pasif ya da bonus yasaklı) dışarıda bırakıldı` : ''}.
                Listeden ad silerseniz yeniden kontrol edin.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <label htmlFor="bulk-bonus-search" className="block text-xs text-slate-400">Üye ara ve seç</label>
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                id="bulk-bonus-search"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={running}
                autoComplete="off"
                spellCheck={false}
                className="admin-input pl-10"
                placeholder="Kullanıcı adı yazın (en az 2 harf)…"
              />
              {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-500" />}
            </div>
            {found.length > 0 && (
              <div className="rounded-xl border border-white/10 bg-black/20 p-2">
                <div className="mb-2 flex items-center gap-2 px-1 text-xs text-slate-500">
                  <span>{found.length} sonuç · tıklayınca listeye eklenir</span>
                  <button
                    type="button"
                    disabled={running}
                    onClick={() => setText((current) => addUsernames(current, found.map((row) => row.username)))}
                    className="ml-auto inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200"
                  >
                    <Plus size={12} /> Tümünü ekle
                  </button>
                </div>
                <div className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
                  {found.map((row) => {
                    const picked = typedKeys.has(row.username.toLocaleLowerCase('tr'));
                    return (
                      <button
                        key={row.id}
                        type="button"
                        disabled={running}
                        aria-pressed={picked}
                        onClick={() => setText((current) => toggleUsername(current, row.username))}
                        className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs font-medium ${picked ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300' : 'border-white/10 text-slate-300 hover:border-cyan-400/40'}`}
                        title={row.is_banned ? 'Engelli hesap' : undefined}
                      >
                        {picked ? <Check size={12} /> : <Plus size={12} />}
                        {row.username}
                        {row.is_banned && <span className="text-red-300">(engelli)</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {search.trim().replace(/^@+/, '').length >= 2 && !searching && !found.length && (
              <p className="px-1 text-xs text-slate-500">Bu adla eşleşen üye yok.</p>
            )}
          </div>
          <label htmlFor="bulk-bonus-names" className="block text-xs text-slate-400">
            Liste — seçtikleriniz buraya eklenir; elle de yapıştırabilirsiniz (alt alta, virgül ya da boşlukla)
          </label>
          <textarea
            id="bulk-bonus-names"
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={running}
            spellCheck={false}
            className="admin-input min-h-64 font-mono text-sm"
            placeholder={'Kozan1234\nsanalye\nMustafareis61'}
          />
          <button type="button" onClick={checkList} disabled={checking || running} className="admin-btn-secondary inline-flex w-full items-center justify-center gap-2">
            {checking ? <Loader2 size={14} className="animate-spin" /> : <ListChecks size={14} />} Listeyi Kontrol Et
          </button>

          {preview && (
            <div className="space-y-2 text-sm" aria-live="polite">
              <div className="flex flex-wrap gap-2">
                <span className="badge-pill badge-green">{preview.found.length} üye bulundu</span>
                {preview.missing.length > 0 && <span className="badge-pill badge-red">{preview.missing.length} bulunamadı</span>}
                {preview.duplicates > 0 && <span className="badge-pill badge-yellow">{preview.duplicates} tekrar ayıklandı</span>}
              </div>
              {preview.missing.length > 0 && (
                <div className="rounded-xl border border-red-400/20 bg-red-400/5 p-3 text-xs text-red-200">
                  <div className="mb-1 flex items-center gap-2 font-semibold">
                    <AlertTriangle size={13} /> Bu adlarla üye yok — bonus verilmez:
                    <button type="button" onClick={copyMissing} className="icon-btn view ml-auto" title="Bulunamayanları kopyala" aria-label="Bulunamayanları kopyala"><Copy size={12} /></button>
                  </div>
                  <div className="break-words font-mono">{preview.missing.join(', ')}</div>
                </div>
              )}
              {preview.found.some((row) => row.is_banned) && (
                <div className="rounded-xl border border-amber-400/20 bg-amber-400/5 p-3 text-xs text-amber-200">
                  Engelli hesaplar listede: {preview.found.filter((row) => row.is_banned).map((row) => row.username).join(', ')}. Bonus onlara da verilir.
                </div>
              )}
            </div>
          )}
        </section>

        <section className="admin-card space-y-4 p-5">
          <h2 className="font-bold">2. Verilecek bonus</h2>
          <div className="grid grid-cols-2 rounded-xl border border-white/10 bg-black/20 p-1">
            <button type="button" disabled={running} onClick={() => setGrant({ ...grant, mode: 'campaign', amount: isFreespinOnlyBonus(selectedCampaign) ? 0 : (Number(grant.amount) > 0 ? grant.amount : blankGrant.amount) })} className={`rounded-lg px-3 py-2 text-sm font-semibold ${grant.mode === 'campaign' ? 'bg-cyan-400/15 text-cyan-300' : 'text-slate-500'}`}>Mevcut Kampanya</button>
            <button type="button" disabled={running} onClick={() => setGrant({ ...grant, mode: 'custom', amount: Number(grant.amount) > 0 ? grant.amount : blankGrant.amount })} className={`rounded-lg px-3 py-2 text-sm font-semibold ${grant.mode === 'custom' ? 'bg-violet-400/15 text-violet-300' : 'text-slate-500'}`}>Özel Bonus</button>
          </div>
          {grant.mode === 'campaign' ? (
            <div>
              <label htmlFor="bulk-bonus-campaign" className="mb-1 block text-xs text-slate-400">Bonus Kampanyası</label>
              <select id="bulk-bonus-campaign" value={grant.bonus_id} disabled={running} onChange={(e) => { const campaign = bonuses.find((item) => item.id === e.target.value); setGrant({ ...grant, bonus_id: e.target.value, amount: isFreespinOnlyBonus(campaign) ? 0 : (Number(grant.amount) > 0 ? grant.amount : blankGrant.amount) }); }} className="admin-input">
                <option value="">Kampanya seçin</option>
                {bonuses.map((bonus) => <option key={bonus.id} value={bonus.id}>{bonus.title}{isFreespinOnlyBonus(bonus) ? ` · ${freespinRewardText(bonus)}` : ` · x${bonus.wager_multiplier}`}{bonus.active ? '' : ' (pasif)'}</option>)}
              </select>
            </div>
          ) : (
            <>
              <div>
                <label htmlFor="bulk-bonus-title" className="mb-1 block text-xs text-slate-400">Özel Bonus Başlığı</label>
                <input id="bulk-bonus-title" value={grant.title} disabled={running} onChange={(e) => setGrant({ ...grant, title: e.target.value })} className="admin-input" placeholder="Örn. 1K Nakit Liste Etkinliği" />
              </div>
              <div>
                <label htmlFor="bulk-bonus-wager" className="mb-1 block text-xs text-slate-400">Çevrim Çarpanı (x)</label>
                <input id="bulk-bonus-wager" type="number" min="0" max="1000" step="1" value={grant.wager_multiplier} disabled={running} onChange={(e) => setGrant({ ...grant, wager_multiplier: e.target.value })} className="admin-input" />
              </div>
            </>
          )}
          {!freespinOnly && (
            <div>
              <label htmlFor="bulk-bonus-amount" className="mb-1 block text-xs text-slate-400">Üye başına tutar (₺)</label>
              <input id="bulk-bonus-amount" type="number" min="0.01" step="0.01" value={grant.amount} disabled={running} onChange={(e) => setGrant({ ...grant, amount: e.target.value })} className="admin-input" />
            </div>
          )}
          <div className={`rounded-xl border p-3 text-xs ${freespinOnly ? 'border-violet-400/20 bg-violet-400/5 text-violet-200' : multiplier > 0 ? 'border-amber-400/20 bg-amber-400/5 text-amber-300' : 'border-emerald-400/20 bg-emerald-400/5 text-emerald-300'}`}>
            {freespinOnly
              ? `${freespinRewardText(selectedCampaign)} her üyenin sağlayıcı hesabına tanımlanır.`
              : multiplier > 0
                ? `Her üyeye ${formatMoney(amount)} bonus bakiyeye kilitlenir; ${formatMoney(amount * multiplier)} çevrim tamamlanınca ana bakiyeye geçer.`
                : `Her üyeye ${formatMoney(amount)} çevrimsiz olarak doğrudan ana bakiyeye eklenir.`}
            {targets.length > 0 && !freespinOnly && (
              <div className="mt-1 font-semibold">Toplam: {targets.length} üye × {formatMoney(amount)} = {formatMoney(amount * targets.length)}</div>
            )}
          </div>
          <div className="rounded-xl border border-white/10 bg-black/20 p-3">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1"
                checked={grant.balance_limit}
                disabled={running}
                onChange={(e) => setGrant({ ...grant, balance_limit: e.target.checked })}
              />
              <span>
                <span className="block text-sm font-semibold">Bakiyesi olan üyeye verilmesin</span>
                <span className="mt-0.5 block text-xs text-slate-500">
                  Ana + bonus bakiyesi eşiğin üstündeki üye atlanır; bakiye bonusun verildiği anda ölçülür.
                </span>
              </span>
            </label>
            {grant.balance_limit && (
              <div className="mt-3 pl-7">
                <label htmlFor="bulk-bonus-max-balance" className="mb-1 block text-xs text-slate-400">Bonus alabilecek en yüksek bakiye (₺)</label>
                <input
                  id="bulk-bonus-max-balance"
                  type="number"
                  min="0"
                  step="0.01"
                  value={grant.max_balance}
                  disabled={running}
                  onChange={(e) => setGrant({ ...grant, max_balance: e.target.value })}
                  className="admin-input admin-input-w-md"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Eşiğin kendisi geçerlidir: 5 yazılırsa bakiyesi 5,00 ₺ olan alır, 5,01 ₺ olan almaz. 0 = bakiyesi tamamen bitmiş olmalı.
                </p>
              </div>
            )}
          </div>
          <div>
            <label htmlFor="bulk-bonus-note" className="mb-1 block text-xs text-slate-400">Yönetici Notu (opsiyonel)</label>
            <textarea id="bulk-bonus-note" value={grant.note} disabled={running} onChange={(e) => setGrant({ ...grant, note: e.target.value })} className="admin-input min-h-16" />
          </div>
          <button type="button" disabled={running || !targets.length} onClick={openConfirm} className="admin-btn-primary w-full py-3">
            {running && <Loader2 size={14} className="mr-2 inline animate-spin" />}
            {targets.length ? `${targets.length} üyeye bonus ver` : 'Önce listeyi kontrol edin'}
          </button>
        </section>
      </div>

      {(running || results.length > 0) && (
        <section className="admin-card space-y-3 p-5" aria-live="polite">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-bold">Sonuç</h2>
            <span className="badge-pill badge-green">{okRows.length} verildi</span>
            {replayCount > 0 && <span className="badge-pill badge-blue">{replayCount} zaten almıştı</span>}
            {skippedRows.length > 0 && <span className="badge-pill badge-yellow">{skippedRows.length} bakiye eşiği nedeniyle atlandı</span>}
            {failedRows.length > 0 && <span className="badge-pill badge-red">{failedRows.length} başarısız</span>}
            {!running && failedRows.length > 0 && (
              <button type="button" onClick={() => run(failedRows.map((row) => row.user_id))} className="admin-btn-secondary ml-auto inline-flex items-center gap-2">
                <RotateCcw size={14} /> Başarısızları yeniden dene
              </button>
            )}
          </div>
          {running && (
            <div>
              <div className="mb-1 text-xs text-slate-400">{progress.done} / {progress.total} üye işlendi · bitene kadar bu sayfayı kapatmayın</div>
              <div className="h-2 overflow-hidden rounded-full bg-white/10" role="progressbar" aria-valuemin={0} aria-valuemax={progress.total} aria-valuenow={progress.done}>
                <div className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-violet-400 transition-all" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
              </div>
            </div>
          )}
          {failedRows.length > 0 && (
            <ul className="space-y-1 text-xs">
              {failedRows.map((row) => (
                <li key={row.user_id} className="flex items-start gap-2 text-red-200">
                  <XCircle size={13} className="mt-0.5 shrink-0" />
                  <span><b>{nameById[row.user_id] || row.username || row.user_id}</b> — {row.error}</span>
                </li>
              ))}
            </ul>
          )}
          {skippedRows.length > 0 && (
            <details className="text-xs text-amber-200">
              <summary className="cursor-pointer">Atlanan üyeler ({skippedRows.length})</summary>
              <ul className="mt-1 space-y-1">
                {skippedRows.map((row) => (
                  <li key={row.user_id}>
                    <b>{nameById[row.user_id] || row.username || row.user_id}</b> — {row.error}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {!running && okRows.length > 0 && !failedRows.length && !skippedRows.length && (
            <div className="flex items-center gap-2 text-sm text-emerald-300"><CheckCircle2 size={15} /> Listedeki bütün üyelere bonus tanımlandı.</div>
          )}
        </section>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Toplu bonusu onayla"
        description={(freespinOnly
          ? `${targets.length} üyeye ${freespinRewardText(selectedCampaign)} tanımlanacak.`
          : `${targets.length} üyeye ${formatMoney(amount)} bonus verilecek (toplam ${formatMoney(amount * targets.length)}).`)
          + (grant.balance_limit ? ` Bakiyesi ${formatMoney(Number(grant.max_balance || 0))} üstünde olan üyeler atlanır.` : '')}
        confirmLabel="Bonusları ver"
        confirmPhrase={targets.length >= CONFIRM_PHRASE_FROM ? 'ONAYLIYORUM' : ''}
        tone="warning"
        busy={running}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => run(targets.map((row) => row.id))}
      />
    </div>
  );
};

export default BulkBonus;
